import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

function BuildYourHouse() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    landArea: "",
    floors: "G",
    bhk: "2BHK",
    budget: "",
    layoutPreference: "Modern Compact",
    materialPreference: "Mixed",
    interiorDesign: "yes",
    expectedCompletion: "6-9 months",
    bathrooms: "2",
    parking: "1 Car",
    vastuPreference: "No Preference",
  });

  const [estimate, setEstimate] = useState(null);

  const presets = useMemo(
    () => ({
      bhkOptions: ["1BHK", "2BHK", "3BHK", "4BHK", "5BHK+"],
      floorsOptions: [
        { value: "G", label: "Ground Floor" },
        { value: "G+1", label: "Ground + 1" },
        { value: "G+2", label: "Ground + 2" },
        { value: "G+3", label: "Ground + 3" },
        { value: "G+4", label: "Ground + 4" },
      ],
      layoutOptions: [
        "Modern Compact",
        "Family Spacious",
        "Rental Focused",
        "Luxury Layout",
        "Duplex Style",
      ],
      materialOptions: ["Standard", "Premium", "Mixed"],
      completionOptions: [
        "3-6 months",
        "6-9 months",
        "9-12 months",
        "12-18 months",
      ],
      bathroomOptions: ["1", "2", "3", "4", "5+"],
      parkingOptions: ["No Parking", "1 Car", "2 Cars", "3+ Cars"],
      vastuOptions: ["No Preference", "Need Vastu Friendly Plan"],
    }),
    []
  );

  const getFloorCount = (floorValue) => {
    if (floorValue === "G") return 1;
    if (floorValue === "G+1") return 2;
    if (floorValue === "G+2") return 3;
    if (floorValue === "G+3") return 4;
    if (floorValue === "G+4") return 5;
    return 1;
  };

  const handleChange = (e) => {
    setForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const landArea = Number(form.landArea || 0);
    const floors = getFloorCount(form.floors);
    const budget = Number(form.budget || 0);

    let perSqFtRate = 2200;
    if (form.materialPreference === "Standard") perSqFtRate = 2000;
    if (form.materialPreference === "Mixed") perSqFtRate = 2200;
    if (form.materialPreference === "Premium") perSqFtRate = 2700;

    let layoutFactor = 1;
    if (form.layoutPreference === "Luxury Layout") layoutFactor = 1.18;
    if (form.layoutPreference === "Rental Focused") layoutFactor = 0.95;
    if (form.layoutPreference === "Duplex Style") layoutFactor = 1.12;

    let bathroomFactor = 1;
    const bathrooms =
      form.bathrooms === "5+" ? 5 : Number(form.bathrooms || 2);
    if (bathrooms >= 3) bathroomFactor += 0.06;
    if (bathrooms >= 4) bathroomFactor += 0.05;

    let parkingFactor = 1;
    if (form.parking === "1 Car") parkingFactor = 1.03;
    if (form.parking === "2 Cars") parkingFactor = 1.06;
    if (form.parking === "3+ Cars") parkingFactor = 1.1;

    const roughConstructionCost =
      landArea *
      floors *
      perSqFtRate *
      layoutFactor *
      bathroomFactor *
      parkingFactor;

    const interiorAddon =
      form.interiorDesign === "yes" ? landArea * floors * 350 : 0;

    const totalEstimate = Math.round(roughConstructionCost + interiorAddon);

    let months = Math.max(4, floors * 3);
    if (form.layoutPreference === "Luxury Layout") months += 2;
    if (form.interiorDesign === "yes") months += 1;

    const budgetGap = budget - totalEstimate;

    setEstimate({
      totalEstimate,
      months,
      fitsBudget: budget >= totalEstimate,
      budgetGap,
      perSqFtRate,
      roughConstructionCost: Math.round(roughConstructionCost),
      interiorAddon: Math.round(interiorAddon),
    });
  };

  return (
    <div
      style={{
        padding: "40px",
        fontFamily: "Arial, sans-serif",
        maxWidth: "1100px",
        margin: "0 auto",
        background: "#000",
        color: "#fff",
        minHeight: "100vh",
      }}
    >
      <button onClick={() => navigate("/")} style={btn}>
        Back to Home
      </button>

      <div style={heroCard}>
        <h1 style={{ marginTop: 0 }}>Build Your House</h1>
        <p style={{ color: "#d1d1d1", lineHeight: "1.8", marginBottom: 0 }}>
          Plan your home with a structured estimate using land size, floor type,
          layout style, materials, interiors, and timeline preferences.
        </p>
      </div>

      <form onSubmit={handleSubmit} style={{ marginTop: "24px" }}>
        <div style={grid}>
          <div style={card}>
            <h2 style={sectionTitle}>Project Basics</h2>

            <label style={label}>Land Area (sq ft)</label>
            <input
              name="landArea"
              type="number"
              placeholder="Example: 1200"
              value={form.landArea}
              onChange={handleChange}
              required
              style={input}
            />

            <label style={label}>Floor Type</label>
            <select
              name="floors"
              value={form.floors}
              onChange={handleChange}
              style={input}
            >
              {presets.floorsOptions.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>

            <label style={label}>BHK / Layout Type</label>
            <select
              name="bhk"
              value={form.bhk}
              onChange={handleChange}
              style={input}
            >
              {presets.bhkOptions.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>

            <label style={label}>Budget (₹)</label>
            <input
              name="budget"
              type="number"
              placeholder="Example: 3500000"
              value={form.budget}
              onChange={handleChange}
              required
              style={input}
            />
          </div>

          <div style={card}>
            <h2 style={sectionTitle}>Design Preferences</h2>

            <label style={label}>Layout Preference</label>
            <select
              name="layoutPreference"
              value={form.layoutPreference}
              onChange={handleChange}
              style={input}
            >
              {presets.layoutOptions.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>

            <label style={label}>Material Preference</label>
            <select
              name="materialPreference"
              value={form.materialPreference}
              onChange={handleChange}
              style={input}
            >
              {presets.materialOptions.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>

            <label style={label}>Include Interior Design</label>
            <select
              name="interiorDesign"
              value={form.interiorDesign}
              onChange={handleChange}
              style={input}
            >
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>

            <label style={label}>Expected Completion Target</label>
            <select
              name="expectedCompletion"
              value={form.expectedCompletion}
              onChange={handleChange}
              style={input}
            >
              {presets.completionOptions.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </div>

          <div style={card}>
            <h2 style={sectionTitle}>Additional Preferences</h2>

            <label style={label}>Bathrooms</label>
            <select
              name="bathrooms"
              value={form.bathrooms}
              onChange={handleChange}
              style={input}
            >
              {presets.bathroomOptions.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>

            <label style={label}>Parking</label>
            <select
              name="parking"
              value={form.parking}
              onChange={handleChange}
              style={input}
            >
              {presets.parkingOptions.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>

            <label style={label}>Vastu Preference</label>
            <select
              name="vastuPreference"
              value={form.vastuPreference}
              onChange={handleChange}
              style={input}
            >
              {presets.vastuOptions.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>

            <button type="submit" style={solidBtn}>
              Generate Estimate
            </button>
          </div>
        </div>
      </form>

      {estimate && (
        <div style={{ ...card, marginTop: "24px" }}>
          <h2 style={{ marginTop: 0 }}>Estimated Project Summary</h2>

          <div style={summaryGrid}>
            <div style={summaryBox}>
              <div style={summaryLabel}>Estimated Project Cost</div>
              <div style={summaryValue}>
                ₹{estimate.totalEstimate.toLocaleString()}
              </div>
            </div>

            <div style={summaryBox}>
              <div style={summaryLabel}>Estimated Timeline</div>
              <div style={summaryValue}>{estimate.months} months</div>
            </div>

            <div style={summaryBox}>
              <div style={summaryLabel}>Construction Base</div>
              <div style={summaryValue}>
                ₹{estimate.roughConstructionCost.toLocaleString()}
              </div>
            </div>

            <div style={summaryBox}>
              <div style={summaryLabel}>Interior Add-on</div>
              <div style={summaryValue}>
                ₹{estimate.interiorAddon.toLocaleString()}
              </div>
            </div>
          </div>

          <div
            style={{
              marginTop: "18px",
              padding: "18px",
              borderRadius: "12px",
              background: estimate.fitsBudget ? "#0f2416" : "#2a1111",
              border: estimate.fitsBudget
                ? "1px solid #1f5d34"
                : "1px solid #6b1d1d",
            }}
          >
            <p style={{ margin: 0 }}>
              <strong>Budget Fit:</strong>{" "}
              {estimate.fitsBudget
                ? "Within or near your stated budget"
                : "Likely above your current stated budget"}
            </p>

            <p style={{ margin: "10px 0 0 0" }}>
              <strong>Budget Difference:</strong>{" "}
              ₹{Math.abs(estimate.budgetGap).toLocaleString()}{" "}
              {estimate.budgetGap >= 0 ? "buffer available" : "extra required"}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

const btn = {
  marginBottom: "20px",
  padding: "10px 16px",
  border: "1px solid #444",
  background: "#111",
  color: "white",
  borderRadius: "8px",
  cursor: "pointer",
};

const heroCard = {
  background: "#111",
  padding: "24px",
  borderRadius: "16px",
  border: "1px solid #2a2a2a",
};

const grid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  gap: "18px",
};

const card = {
  background: "#111",
  padding: "22px",
  borderRadius: "14px",
  border: "1px solid #2a2a2a",
};

const sectionTitle = {
  marginTop: 0,
  color: "#fff",
};

const label = {
  display: "block",
  marginBottom: "8px",
  marginTop: "12px",
  color: "#fff",
};

const input = {
  width: "100%",
  padding: "12px",
  borderRadius: "8px",
  border: "1px solid #2a2a2a",
  background: "#0b0b0b",
  color: "#fff",
  boxSizing: "border-box",
};

const solidBtn = {
  marginTop: "18px",
  padding: "12px 18px",
  border: "none",
  background: "#fff",
  color: "#000",
  borderRadius: "8px",
  cursor: "pointer",
  fontWeight: "bold",
  width: "100%",
};

const summaryGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "14px",
  marginTop: "18px",
};

const summaryBox = {
  background: "#0b0b0b",
  border: "1px solid #222",
  padding: "16px",
  borderRadius: "12px",
};

const summaryLabel = {
  color: "#aaa",
  fontSize: "14px",
};

const summaryValue = {
  color: "#fff",
  fontSize: "22px",
  fontWeight: "bold",
  marginTop: "6px",
};

export default BuildYourHouse;